import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function Novedades() {
  const [novedades, setNovedades] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try { const res = await api.get('/notificaciones/novedades'); setNovedades(res.data); }
    catch { /* silent */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const marcarLeida = async (id) => {
    try {
      await api.post(`/notificaciones/novedades/${id}/leer`);
      setNovedades(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
    } catch { toast.error('Error'); }
  };

  const tipoColor = {
    mejora: 'bg-emerald-500/20 text-emerald-400 border-emerald-700',
    fix: 'bg-amber-500/20 text-amber-400 border-amber-700',
    nuevo: 'bg-cyan-500/20 text-cyan-400 border-cyan-700',
    importante: 'bg-red-500/20 text-red-400 border-red-700',
  };

  if (cargando) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="novedades-page">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-bold text-slate-100 font-barlow">Novedades</h2>
      </div>
      {novedades.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-8 text-center">
            <Bell className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No hay novedades por el momento</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {novedades.map(n => (
            <Card key={n.id} className={`border-slate-800 transition-all ${n.leida ? 'bg-slate-900/20' : 'bg-slate-900/60 border-l-2 border-l-amber-500'}`}
              data-testid={`novedad-card-${n.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={tipoColor[n.tipo] || tipoColor.mejora + ' text-[10px]'}>{n.tipo}</Badge>
                    <h3 className="text-sm font-bold text-slate-200">{n.titulo}</h3>
                  </div>
                  {!n.leida && (
                    <Button variant="ghost" size="sm" onClick={() => marcarLeida(n.id)}
                      className="text-xs text-slate-500 hover:text-slate-300" data-testid={`marcar-leida-${n.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Marcar leida
                    </Button>
                  )}
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{n.contenido}</p>
                <p className="text-[10px] text-slate-600 mt-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {new Date(n.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
